import React, { useCallback, useRef, useState, useEffect } from 'react';
import classNames from 'classnames';
import { dynamicDebounce } from '@trello/time';
import { EnterpriseDashboardTestIds } from '@trello/webdriverio-tests';
import Icon, { IconName } from 'app/common/components/controls/icon';
import { QueryContext } from './QueryContext';

import styles from './Search.less';

const useFocus = (el: HTMLInputElement | null) => {
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (el) {
      const onFocus = () => setIsFocused(true);
      const onBlur = () => setIsFocused(false);
      el.addEventListener('focus', onFocus);
      el.addEventListener('blur', onBlur);
      el.onblur = onBlur;

      return () => {
        el.removeEventListener('focus', onFocus);
        el.removeEventListener('blur', onBlur);
      };
    }
  }, [el]);

  return isFocused;
};

interface SearchProps {
  autofocus?: boolean;
  placeholder?: string;
  onChange: (s: string) => void;
  searchCount?: number;
  debounce?: boolean;
}

export const Search = (props: SearchProps) => {
  const { placeholder, onChange, searchCount, debounce } = props;

  const { query, setQuery } = React.useContext(QueryContext);

  const inputRef = useRef<HTMLInputElement>(null);

  const isFocused = useFocus(inputRef.current);

  /*
   * We want the dynamicDebounce to be able to access the latest
   * value for searchCount, but the `debouncedOnChange` function
   * cannot be re-created when searchCount changes, because we need
   * that reference to never change. While class-based components could
   * reference this.props.searchCount inside of the dynamicDebounce callback,
   * we can't do that with hooks since we don't have a stable `this` reference.
   * `props` is not a stable reference across renders. So we use this
   * searchCountRef to provide the reference value dynamicDebounce needs
   * to access searchCount.
   */
  const searchCountRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    searchCountRef.current = searchCount;
  }, [searchCount]);

  const debouncedOnChange = useCallback(
    dynamicDebounce(
      (_query: string, _: boolean = false) => {
        onChange(_query);
      },
      (_query: string, noPenalty: boolean = false) => {
        const _searchCount = searchCountRef.current;
        const baseDelayMs = 300;

        if (noPenalty) {
          return baseDelayMs;
        }

        // Increase debounce time for short queries and large collections
        let penalty = 1;

        if (_query.length < 3) {
          penalty++;
        }
        if (_query.length < 2) {
          penalty++;
        }
        if (_searchCount && _searchCount > 100) {
          penalty++;
        }
        if (_searchCount && _searchCount > 500) {
          penalty++;
        }
        return baseDelayMs * penalty;
      },
    ),
    [],
  );
  useEffect(() => {
    return () => debouncedOnChange.clear();
  }, [debouncedOnChange]);

  const _onChange = React.useCallback(
    (evt: React.ChangeEvent<HTMLInputElement>) => {
      setQuery(evt.target.value);
      if (debounce) {
        debouncedOnChange(evt.target.value);
      } else {
        onChange(evt.target.value);
      }
    },
    [debounce, debouncedOnChange, onChange, setQuery],
  );

  const onClear = React.useCallback(() => {
    setQuery('');
    if (debounce) {
      debouncedOnChange('', true);
    } else {
      onChange('');
    }
  }, [debounce, debouncedOnChange, onChange, setQuery]);

  const onKeyDown = React.useCallback(
    (evt: React.KeyboardEvent) => {
      if (evt.key === 'Enter') {
        if (debounce) {
          debouncedOnChange(query, true);
        } else {
          onChange(query);
        }
      }
    },
    [debounce, debouncedOnChange, onChange, query],
  );

  return (
    <div className={styles.search}>
      <input
        type="text"
        value={query}
        ref={inputRef}
        placeholder={placeholder}
        onChange={_onChange}
        onKeyDown={onKeyDown}
      />
      {query.length > 0 && (
        <span
          onClick={onClear}
          role="button"
          data-test-id={EnterpriseDashboardTestIds.SearchCloseIcon}
        >
          <Icon
            name={IconName.Close}
            className={classNames(
              styles.searchClose,
              isFocused && styles.active,
            )}
          />
        </span>
      )}
    </div>
  );
};
